import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getHealthHouseMeasurementsByVisitId } from '../api/reportApi';
import {
  buildHealthHouseCheckRows,
  formatHealthHouseResultValue
} from '../utils/healthHouseCheckDisplay';
import { formatDateTime, normalizeUiLocale } from '../utils/locale';

const HealthHouseCheckList = ({ visitId }) => {
  const { t, i18n } = useTranslation();
  const locale = normalizeUiLocale(i18n.resolvedLanguage || i18n.language);
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(Boolean(visitId));
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let active = true;

    if (!visitId) {
      setMeasurements([]);
      setLoading(false);
      setHasError(false);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    setHasError(false);

    getHealthHouseMeasurementsByVisitId(visitId)
      .then((data) => {
        if (active) {
          setMeasurements(Array.isArray(data) ? data : []);
        }
      })
      .catch((error) => {
        console.error(`获取就诊ID${visitId}的健康小屋检查列表失败:`, error);
        if (active) {
          setMeasurements([]);
          setHasError(true);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [visitId]);

  const rows = useMemo(
    () => buildHealthHouseCheckRows(measurements, t),
    [measurements, t]
  );

  const formatCheckTime = (value) => {
    if (!value) return t('common.unknown');
    return formatDateTime(value, locale) || value;
  };

  const renderResult = (row) => {
    if (row.results.length === 1) {
      const result = row.results[0];
      return (
        <div className="health-house-checks__single-result">
          <span>{result.label}</span>
          <strong>{formatHealthHouseResultValue(result)}</strong>
        </div>
      );
    }

    return (
      <table
        className="health-house-checks__result-table"
        aria-label={t('reports.healthHouseResultTableLabel', { item: row.itemName })}
      >
        <thead>
          <tr>
            <th>{t('reports.healthHouseResultItem')}</th>
            <th>{t('reports.healthHouseResultValue')}</th>
          </tr>
        </thead>
        <tbody>
          {row.results.map((result) => (
            <tr key={result.key}>
              <td>{result.label}</td>
              <td>{formatHealthHouseResultValue(result)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <section className="report-detail-page__section health-house-checks-section">
      <h3>{t('reports.healthHouseChecksList')}</h3>

      {loading ? (
        <div className="health-house-checks__state" role="status">
          {t('reports.loadingHealthHouseChecks')}
        </div>
      ) : hasError ? (
        <div className="health-house-checks__state health-house-checks__state--error" role="alert">
          {t('reports.fetchHealthHouseChecksError')}
        </div>
      ) : rows.length === 0 ? (
        <div className="health-house-checks__state">
          {t('reports.noHealthHouseChecks')}
        </div>
      ) : (
        <div className="health-house-checks__scroll">
          <table className="paper-table health-house-checks__table">
            <thead>
              <tr>
                <th>{t('reports.healthHouseCheckItem')}</th>
                <th>{t('reports.healthHouseCheckTime')}</th>
                <th>{t('reports.healthHouseCheckResult')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id || row.code}>
                  <td>
                    <strong className="health-house-checks__item-name">{row.itemName}</strong>
                  </td>
                  <td className="health-house-checks__time">{formatCheckTime(row.checkedAt)}</td>
                  <td>{renderResult(row)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default HealthHouseCheckList;
